import { useMemo } from 'react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { SpinningLoader, Text } from '@frontend/design-system';
import { useActiveConversationSelector } from '../../providers/active-conversation.provider';
import { useTeamChatSelector } from '../../providers/team-chat.provider';
import { ConversationBody } from '../conversation/conversation-body';
import { ThreadTitle } from './thread-title';

export const ThreadPanel = () => {
  return (
    <>
      <ThreadTitle />
      <ThreadBody />
    </>
  );
};

const ThreadBody = () => {
  const { t } = useTranslation('team-chat');
  const selectedThreadParentId = useTeamChatSelector((ctx) => ctx.selectedThreadParentId);
  const parentMessageRef = useTeamChatSelector((ctx) => ctx.parentMessageRef);
  const parentMessageFromConversation = useActiveConversationSelector((ctx) =>
    ctx.messages.find((message) => message.id === selectedThreadParentId)
  );
  const replies = useActiveConversationSelector((ctx) => ctx.replies);
  const repliesError = useActiveConversationSelector((ctx) => ctx.repliesError);
  const fetchPreviousReplies = useActiveConversationSelector((ctx) => ctx.fetchPreviousReplies);
  const isLoadingReplies = useActiveConversationSelector((ctx) => ctx.isLoadingReplies);
  const isFetchingMessages = useActiveConversationSelector((ctx) => ctx.isFetching);
  const isFetchingReplies = useActiveConversationSelector((ctx) => ctx.isFetchingReplies);
  const hasPreviousRepliesPage = useActiveConversationSelector((ctx) => ctx.hasPreviousRepliesPage);

  const parentMessage = useMemo(
    () => parentMessageFromConversation ?? parentMessageRef,
    [parentMessageRef, parentMessageFromConversation]
  );
  const allMessages = useMemo(() => (parentMessage ? [parentMessage, ...replies] : []), [parentMessage, replies]);

  if (isLoadingReplies || isFetchingMessages) {
    return (
      <EmptyPanelWrapper>
        <SpinningLoader />
      </EmptyPanelWrapper>
    );
  }

  if (repliesError) {
    return (
      <EmptyPanelWrapper>
        <Text color='error'>{t('Error loading replies')}</Text>
      </EmptyPanelWrapper>
    );
  }

  if (!parentMessage) {
    return (
      <EmptyPanelWrapper>
        <Text color='error'>{t('Thread parent message not found')}</Text>
      </EmptyPanelWrapper>
    );
  }

  if (parentMessage.type === 'deleted' && parentMessage.replyCount === 0) {
    return (
      <EmptyPanelWrapper>
        <Text color='error'>{t('Parent message has been deleted')}</Text>
      </EmptyPanelWrapper>
    );
  }

  return parentMessage ? (
    <ConversationBody
      selectedTarget={undefined}
      messages={allMessages}
      fetchPreviousPage={fetchPreviousReplies}
      hasPreviousPage={hasPreviousRepliesPage}
      isFetching={isFetchingReplies}
    />
  ) : (
    <SpinningLoader />
  );
};

const EmptyPanelWrapper = ({ children }: { children: React.ReactNode }) => (
  <div css={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: theme.spacing(2) }}>
    {children}
  </div>
);
